export default function useUrlReplace () {
  /**
   * Replace placeholders in a URL with values from an object.
   * @param {string} url - The URL string with placeholders.
   * @param {Record<string, string>} object - The object containing replacement values.
   * @param {boolean} addQueryParams - Whether to add query parameters to the URL.
   * @returns {string} - The URL with placeholders replaced by values.
   */
  function replaceUrl (url: string, object: Record<string, string>, addQueryParams = true): string {
    // Use regular expressions to find and replace placeholders in the URL
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let replacedUrl = (url || '').replace(/:([a-z][a-zA-Z0-9]+)/g, (match, key) => {
      if (object[key] !== undefined) {
        return object[key];
      } else {
        // If a placeholder doesn't have a matching key in the object, replace with blank
        return '';
      }
    });

    // Replace multiple slashes with a single slash
    replacedUrl = replacedUrl.replace(/\/+/g, '/').replace(':/', '://');

    // Check if the original URL had a trailing slash and add it back if needed
    if (url.endsWith('/') && !replacedUrl.endsWith('/')) {
      replacedUrl += '/';
    }
    if (addQueryParams) {
      const unusedProperties = Object.keys(object).reduce((acc, key) => {
        if (!url.includes(`:${key}`)) {
          acc[key] = object[key];
        }
        return acc;
      }, {} as Record<string, string>);

      const queryString = Object.keys(unusedProperties).map((key) => {
        if (!unusedProperties[key]) {
          return '';
        }
        return `${encodeURIComponent(key)}=${encodeURIComponent(unusedProperties[key])}`;
      }).filter(item => item.trim() !== '');

      // Append the query string to the URL
      if (queryString && queryString.length > 0) {
        replacedUrl += `?${queryString.join('&')}`;
      }
    }
    // Remove trailing slash before query parameter if there is one
    replacedUrl = replacedUrl.replace(/\/\?/, '?');

    return replacedUrl;
  }

  return {
    replaceUrl
  };
}
